import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { attachSupabaseToken } from "@/integrations/supabase/attach-token-middleware";
import { supabase as publicSupabase } from "@/integrations/supabase/client";

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

/** Client-side log of a site search. Anonymous inserts are allowed by RLS. */
export async function logSiteSearch(input: {
  query: string;
  resultsCount: number;
  path?: string;
  clickedPath?: string | null;
}) {
  const query = input.query.trim().slice(0, 200);
  if (!query) return;
  try {
    await publicSupabase.from("site_searches").insert({
      query,
      results_count: input.resultsCount,
      path: input.path ?? null,
      clicked_path: input.clickedPath ?? null,
    });
  } catch {
    // Never let analytics break the search experience.
  }
}

export const adminListSearches = createServerFn({ method: "GET" })
  .middleware([attachSupabaseToken, requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("site_searches")
      .select("id,query,results_count,path,clicked_path,created_at")
      .order("created_at", { ascending: false })
      .limit(2000);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const adminDeleteSearch = createServerFn({ method: "POST" })
  .middleware([attachSupabaseToken, requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("site_searches")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
